import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  IconButton,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  CalendarEvent,
  CreateCalendarEventDto,
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  useGetAllBuildings,
  ApiResponse_GetAll_Building,
} from '@/api/endpoints/building/useGetAllBuildings';
import { useCreateCalendarEvent } from '@/api/endpoints/calendar/useCreateCalendarEvent';
import {
  useGetAllTenants,
  ApiResponse_GetAll_Tenant,
} from '@/api/endpoints/tenant/useGetAllTenants';
import {
  useGetAllUnitsInBuilding,
  ApiResponse_GetAllInBuilding_Unit,
} from '@/api/endpoints/unit/useGetAllUnitsInBuilding';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

import FormSelect from '@/components/form/FormSelect';
import FormSwitch from '@/components/form/FormSwitch';
import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

interface Props {
  onClose: () => void;
  propertyLocationId: number;
  buildingId?: number;
  unitId?: number;
  tenantId?: number;
  communityId?: number;
  onEventCreated?: (event: CalendarEvent) => void;
  initialDate?: Date | null;
  initialCategory?: string | null;
  initialTitle?: string;
}

interface ReminderInterval {
  value: number;
  unit: ReminderUnit;
}

interface CalendarEventFormData {
  // Base fields
  title: string;
  description?: string;
  location?: string;
  start: string;
  end: string;
  all_day: boolean;
  property_location_id: number;
  community_id?: number;
  building_id?: number;
  unit_id?: number;
  tenant_id?: number;

  // UI specific fields
  isDateRange: boolean;

  // Category and reminder fields
  category: EventCategory;
  reminderType: ReminderType;
  recurringPattern: RecurringPattern | 'none';
  recurringEvery: number;
  recurringPeriod: RecurringPeriod;
  neverExpires: boolean;
  multipleReminders: boolean;
  reminderIntervals: ReminderInterval[];

  // Notification fields
  notifySelf: boolean;
  notifyTenant: boolean;
  notifyVendor: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
  emailNotification: boolean;
  smsNotification: boolean;
  pushNotification: boolean;
  dashboardNotification: boolean;
}

// Convert form values to match our type enums
const eventCategories = [
  { label: 'Lease', value: EventCategory.LEASE },
  { label: 'Payment', value: EventCategory.PAYMENT },
  { label: 'Maintenance', value: EventCategory.MAINTENANCE },
  { label: 'Inspection', value: EventCategory.INSPECTION },
  { label: 'Other', value: EventCategory.OTHER },
];

const recurringPatterns = [
  { label: 'Does not repeat', value: 'none' },
  { label: 'Daily', value: RecurringPattern.DAILY },
  { label: 'Weekly', value: RecurringPattern.WEEKLY },
  { label: 'Monthly', value: RecurringPattern.MONTHLY },
  { label: 'Yearly', value: RecurringPattern.YEARLY },
  { label: 'Custom...', value: RecurringPattern.CUSTOM },
];

const periodOptions = [
  { label: 'Days', value: RecurringPeriod.DAYS },
  { label: 'Weeks', value: RecurringPeriod.WEEKS },
  { label: 'Months', value: RecurringPeriod.MONTHS },
  { label: 'Years', value: RecurringPeriod.YEARS },
];

const vendors = [
  { label: 'Select vendor', value: '' },
  { label: 'Building Management', value: 'building-management' },
  { label: 'Plumbing Service', value: 'plumbing' },
  { label: 'Electrical Service', value: 'electrical' },
  { label: 'Cleaning Service', value: 'cleaning' },
];

const reminderUnitOptions = [
  { label: 'Days before', value: ReminderUnit.DAYS_BEFORE },
  { label: 'Weeks before', value: ReminderUnit.WEEKS_BEFORE },
  { label: 'Months before', value: ReminderUnit.MONTHS_BEFORE },
];

const CalendarEventCreateModal2: React.FC<Props> = ({
  onClose,
  propertyLocationId,
  buildingId,
  unitId,
  tenantId,
  communityId,
  onEventCreated,
  initialDate,
  initialCategory,
  initialTitle,
}) => {
  const getInitialCategory = () => {
    if (initialCategory === 'hvac') return EventCategory.MAINTENANCE;
    if (initialCategory === 'inspection') return EventCategory.INSPECTION;
    if (initialCategory === 'maintenance') return EventCategory.MAINTENANCE;
    return EventCategory.PAYMENT;
  };
  const form = useForm<CalendarEventFormData>({
    defaultValues: {
      title: initialTitle || '',
      description: '',
      location: '',
      start: (() => {
        const [dateOnly] =
          initialDate?.toISOString().split('T') || new Date().toISOString().split('T');
        return dateOnly;
      })(),
      end: (() => {
        const [dateOnly] =
          initialDate?.toISOString().split('T') || new Date().toISOString().split('T');
        return dateOnly;
      })(),
      all_day: true,
      property_location_id: propertyLocationId,
      community_id: communityId,
      building_id: buildingId,
      unit_id: unitId,
      tenant_id: tenantId,
      isDateRange: false,
      category: getInitialCategory(),
      reminderType: ReminderType.ONE_TIME,
      recurringPattern: 'none' as RecurringPattern | 'none',
      recurringEvery: 1,
      recurringPeriod: RecurringPeriod.DAYS,
      neverExpires: true,
      multipleReminders: false,
      reminderIntervals: [{ value: 1, unit: ReminderUnit.DAYS_BEFORE }],
      notifySelf: true,
      notifyTenant: false,
      notifyVendor: false,
      vendorId: '',
      vendorEmail: '',
      vendorMessage: '',
      emailNotification: true,
      smsNotification: false,
      pushNotification: false,
      dashboardNotification: false,
    },
  });

  const { watch, setValue, getValues } = form;
  const isDateRange = watch('isDateRange');
  const startDate = watch('start');
  const selectedBuildingId = watch('building_id');
  const selectedUnitId = watch('unit_id');
  const recurringPattern = watch('recurringPattern');
  const recurringEvery = watch('recurringEvery');
  const recurringPeriod = watch('recurringPeriod');
  const multipleReminders = watch('multipleReminders');
  const reminderIntervals = watch('reminderIntervals');
  const notifyVendor = watch('notifyVendor');
  const neverExpires = watch('neverExpires');
  const notifySelf = watch('notifySelf');
  const emailNotification = watch('emailNotification');
  const smsNotification = watch('smsNotification');
  const dashboardNotification = watch('dashboardNotification');

  const { data: buildings } = useGetAllBuildings({ location_id: propertyLocationId });
  const { data: units } = useGetAllUnitsInBuilding(
    { buildingId: selectedBuildingId },
    !!selectedBuildingId
  );
  const { data: tenants } = useGetAllTenants({ unit_id: selectedUnitId }, !!selectedUnitId);

  useEffect(() => {
    if (!isDateRange) {
      setValue('end', startDate);
    } else {
      // If switching to date range and end date is before start date, update end date
      const endDate = watch('end');
      if (new Date(endDate) < new Date(startDate)) {
        setValue('end', startDate);
      }
    }
  }, [isDateRange, startDate, setValue, watch]);

  // Reset dependent fields when parent selection changes
  useEffect(() => {
    if (selectedBuildingId) {
      setValue('unit_id', undefined);
      setValue('tenant_id', undefined);
    }
  }, [selectedBuildingId, setValue]);

  useEffect(() => {
    if (selectedUnitId) {
      setValue('tenant_id', undefined);
    }
  }, [selectedUnitId, setValue]);

  const { mutateAsync: createEvent } = useCreateCalendarEvent({
    onSuccess: event => {
      if (onEventCreated) {
        onEventCreated({
          id: event.id,
          title: event.title,
          description: event.description,
          start: event.start,
          end: event.end,
          all_day: event.all_day,
          propertyLocationId: event.propertyLocationId || 0,
          buildingId: event.buildingId,
          unitId: event.unitId,
          tenantId: event.tenantId,
          created_by: event.created_by,
          created_at: event.created_at,
          updated_at: event.updated_at,
        });
      }
      onClose();
    },
  });

  const addReminderInterval = () => {
    const currentIntervals = getValues('reminderIntervals');
    setValue('reminderIntervals', [
      ...currentIntervals,
      { value: 30, unit: ReminderUnit.DAYS_BEFORE },
    ]);
  };

  const removeReminderInterval = (index: number) => {
    const currentIntervals = getValues('reminderIntervals');
    setValue(
      'reminderIntervals',
      currentIntervals.filter((_, i) => i !== index)
    );
  };

  const handleSubmit = (data: CalendarEventFormData) => {
    // Extract only the fields needed for the API from the form data
    const { isDateRange: _, recurringPattern: pattern, location, ...rest } = data;

    // Handle the 'none' pattern case
    const finalPayload: Record<string, unknown> = { ...rest };

    if (pattern === 'none') {
      finalPayload.reminderType = ReminderType.ONE_TIME;
      // Don't include recurring fields when pattern is 'none'
    } else {
      finalPayload.recurringPattern = pattern;
      finalPayload.recurringEvery = data.recurringEvery;
      finalPayload.recurringPeriod = data.recurringPeriod;
      finalPayload.neverExpires = data.neverExpires;
    }

    // Convert form reminderIntervals to the expected CalendarReminderInterval format
    const formattedIntervals = data.reminderIntervals.map(interval => ({
      value: interval.value,
      unit: interval.unit,
    }));

    // Build metadata object with all the metadata fields
    const metadata: Record<string, unknown> = {
      category: data.category,
      notifySelf: data.notifySelf,
      notifyTenant: data.notifyTenant,
      notifyVendor: data.notifyVendor,
      emailNotification: data.emailNotification,
      smsNotification: data.smsNotification,
      pushNotification: data.pushNotification,
      dashboardNotification: data.dashboardNotification,
    };

    // Add vendor info to metadata if notifying vendor
    if (data.notifyVendor && (data.vendorId || data.vendorEmail || data.vendorMessage)) {
      metadata.vendor = {
        id: data.vendorId,
        email: data.vendorEmail,
        message: data.vendorMessage,
      };
    }

    // Add location to metadata if provided
    if (location) {
      metadata.location = location;
    }

    // Only add metadata if it has properties
    if (Object.keys(metadata).length > 0) {
      finalPayload.metadata = metadata;
    }

    const payload = {
      ...finalPayload,
      reminderIntervals: formattedIntervals,
    } as CreateCalendarEventDto;

    return createEvent(payload);
  };

  // Helper function to calculate and display the reminder date
  const calculateReminderDate = (value: number, unit: ReminderUnit) => {
    const startValue = form.watch('start');
    if (!startValue) return '';

    // Get the event start date
    const dt = new Date(startValue);
    const reminderDate = new Date(dt);

    return format(reminderDate, 'MMM d, yyyy');
  };

  // Create styles based on Figma design
  const figmaStyles = {
    formSection: {
      p: 1.5,
      mb: 1.5,
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 1,
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      backgroundColor: '#FFFFFF',
    },
    sectionTitle: {
      mb: 1,
      fontWeight: 500,
      fontSize: '14px',
      color: '#414651',
    },
    inputLabel: {
      fontWeight: 500,
      fontSize: '13px',
      color: '#414651',
      mb: 0.5,
    },
    notificationLabel: {
      '& .MuiFormControlLabel-label': {
        fontSize: '13px',
      },
    },
    inputField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        '& fieldset': {
          borderColor: '#D5D7DA',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 10px',
        fontSize: '13px',
      },
      '& .MuiInputLabel-root': {
        fontSize: '13px',
        fontWeight: 500,
      },
    },
    formFieldContainer: {
      mb: 1,
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 0.5,
    },
    twoColumnLayout: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    inlineGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1,
    },
  };

  return (
    <ModalWithForm
      title="Add Event"
      onClose={onClose}
      maxWidth="sm"
      form={form}
      onSubmit={handleSubmit}
      buttonColor="primary"
      buttonLabel="Add Event"
      showCloseButton={false}
    >
      <MuiPageWrapper>
        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Basic Information
          </Typography>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Title</Typography>
            <FormTextField
              control={form.control}
              textFieldProps={{
                fullWidth: true,
                InputProps: {
                  placeholder: 'e.g., HOA Fee Due, Lease Expiration',
                },
                sx: figmaStyles.inputField,
              }}
              {...form.register('title', {
                required: 'Title is required',
              })}
              error={!!form.formState.errors.title}
              helperText={form.formState.errors.title?.message?.toString()}
            />
          </Box>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Category</Typography>
            <FormSelect
              control={form.control}
              options={eventCategories}
              muiSelectProps={{
                sx: figmaStyles.inputField,
              }}
              {...form.register('category')}
            />
          </Box>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Description</Typography>
            <FormTextField
              name="description"
              control={form.control}
              textFieldProps={{
                fullWidth: true,
                multiline: true,
                minRows: 2,
                maxRows: 6,
                InputProps: {
                  placeholder: 'Enter details about this event or reminder',
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Location
          </Typography>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Event Location</Typography>
            <FormTextField
              name="location"
              control={form.control}
              textFieldProps={{
                fullWidth: true,
                InputProps: {
                  placeholder: 'e.g., Community Center, Pool Area, Meeting Room',
                },
                sx: figmaStyles.inputField,
              }}
            />
          </Box>

          {buildings && buildings.length > 0 && (
            <Box sx={figmaStyles.formFieldContainer}>
              <Typography sx={figmaStyles.inputLabel}>Building</Typography>
              <FormSelect
                control={form.control}
                placeholder="Select building"
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
                options={[
                  { label: 'Select building', value: '' },
                  ...(buildings?.map((building: ApiResponse_GetAll_Building[0]) => ({
                    label: building.name || building.address_text,
                    value: building.id,
                  })) || []),
                ]}
                {...form.register('building_id', {
                  setValueAs: value => (value === '' ? undefined : Number(value)),
                })}
              />
            </Box>
          )}

          {selectedBuildingId && units && units.length > 0 && (
            <Box sx={figmaStyles.formFieldContainer}>
              <Typography sx={figmaStyles.inputLabel}>Unit</Typography>
              <FormSelect
                control={form.control}
                placeholder="Select unit"
                disabled={!selectedBuildingId}
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
                options={[
                  { label: 'Select unit', value: '' },
                  ...(units?.map((unit: ApiResponse_GetAllInBuilding_Unit[0]) => ({
                    label: unit.name,
                    value: unit.id,
                  })) || []),
                ]}
                {...form.register('unit_id', {
                  setValueAs: value => (value === '' ? undefined : Number(value)),
                })}
              />
            </Box>
          )}

          {selectedUnitId && tenants && tenants.length > 0 && (
            <Box sx={figmaStyles.formFieldContainer}>
              <Typography sx={figmaStyles.inputLabel}>Tenant</Typography>
              <FormSelect
                control={form.control}
                placeholder="Select tenant"
                disabled={!selectedUnitId}
                muiSelectProps={{
                  sx: figmaStyles.inputField,
                }}
                options={[
                  { label: 'Select tenant', value: '' },
                  ...(tenants?.map((tenant: ApiResponse_GetAll_Tenant[0]) => ({
                    label: tenant.full_name,
                    value: tenant.id,
                  })) || []),
                ]}
                {...form.register('tenant_id', {
                  setValueAs: value => (value === '' ? undefined : Number(value)),
                })}
              />
            </Box>
          )}
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Schedule
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormSwitch name="isDateRange" control={form.control} controlLabel="Multi-day event" />
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormTextField
                label={isDateRange ? 'Start Date' : 'Event Date'}
                name="start"
                control={form.control}
                textFieldProps={{
                  fullWidth: true,
                  type: 'date',
                  InputLabelProps: { shrink: true },
                  sx: figmaStyles.inputField,
                }}
              />
            </Box>
            {isDateRange && (
              <Box sx={{ flex: 1 }}>
                <FormTextField
                  label="End Date"
                  name="end"
                  control={form.control}
                  textFieldProps={{
                    fullWidth: true,
                    type: 'date',
                    InputLabelProps: { shrink: true },
                    inputProps: {
                      min: startDate,
                    },
                    sx: figmaStyles.inputField,
                  }}
                />
              </Box>
            )}
          </Stack>

          <Box sx={figmaStyles.formFieldContainer}>
            <Typography sx={figmaStyles.inputLabel}>Repeat</Typography>
            <FormSelect
              control={form.control}
              muiSelectProps={{
                sx: figmaStyles.inputField,
              }}
              options={recurringPatterns}
              {...form.register('recurringPattern', {
                onChange: e => {
                  const pattern = e.target.value;
                  if (pattern === 'none') {
                    setValue('reminderType', ReminderType.ONE_TIME);
                    setValue('neverExpires', true);
                  } else {
                    setValue('reminderType', ReminderType.RECURRING);
                    setValue('neverExpires', false);
                    // Set sensible defaults based on pattern
                    if (pattern === RecurringPattern.DAILY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.DAYS);
                    } else if (pattern === RecurringPattern.WEEKLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.WEEKS);
                    } else if (pattern === RecurringPattern.MONTHLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.MONTHS);
                    } else if (pattern === RecurringPattern.YEARLY) {
                      setValue('recurringEvery', 1);
                      setValue('recurringPeriod', RecurringPeriod.YEARS);
                    }
                  }
                },
              })}
            />
          </Box>

          {recurringPattern !== 'none' && (
            <>
              {recurringPattern === RecurringPattern.CUSTOM && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>
                    Custom Recurrence
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: '13px', color: '#414651' }}>Every</Typography>
                    <FormTextField
                      control={form.control}
                      textFieldProps={{
                        type: 'number',
                        inputProps: { min: 1 },
                        sx: { ...figmaStyles.inputField, width: 80 },
                      }}
                      {...form.register('recurringEvery', {
                        setValueAs: value => (value === '' ? 1 : Number(value)),
                      })}
                    />
                    <FormSelect
                      control={form.control}
                      muiSelectProps={{
                        sx: { ...figmaStyles.inputField, minWidth: 120 },
                      }}
                      options={periodOptions}
                      {...form.register('recurringPeriod')}
                    />
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>Ends</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={neverExpires}
                        onChange={e => setValue('neverExpires', e.target.checked)}
                      />
                    }
                    label="Never"
                    sx={figmaStyles.notificationLabel}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!neverExpires}
                        onChange={e => setValue('neverExpires', !e.target.checked)}
                      />
                    }
                    label="On date"
                    sx={figmaStyles.notificationLabel}
                  />
                  {!neverExpires && (
                    <Box sx={{ ml: 4 }}>
                      <FormTextField
                        label="End Date"
                        name="end"
                        control={form.control}
                        textFieldProps={{
                          fullWidth: true,
                          type: 'date',
                          InputLabelProps: { shrink: true },
                          inputProps: {
                            min: startDate,
                          },
                          sx: { ...figmaStyles.inputField, maxWidth: 200 },
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Show next occurrences preview */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
                  Next occurrences:
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                  {(() => {
                    // Parse the date string in local timezone to avoid UTC issues
                    const [year, month, day] = startDate.split('-').map(Number);
                    const baseDate = new Date(year, month - 1, day);
                    const dates = [];
                    for (let i = 1; i <= 3; i++) {
                      let nextDate = new Date(baseDate);
                      const isDailyPattern =
                        recurringPattern === RecurringPattern.DAILY ||
                        (recurringPattern === RecurringPattern.CUSTOM &&
                          recurringPeriod === RecurringPeriod.DAYS);
                      const isWeeklyPattern =
                        recurringPattern === RecurringPattern.WEEKLY ||
                        (recurringPattern === RecurringPattern.CUSTOM &&
                          recurringPeriod === RecurringPeriod.WEEKS);
                      const isMonthlyPattern =
                        recurringPattern === RecurringPattern.MONTHLY ||
                        (recurringPattern === RecurringPattern.CUSTOM &&
                          recurringPeriod === RecurringPeriod.MONTHS);
                      const isYearlyPattern =
                        recurringPattern === RecurringPattern.YEARLY ||
                        (recurringPattern === RecurringPattern.CUSTOM &&
                          recurringPeriod === RecurringPeriod.YEARS);

                      const multiplier = i * (recurringEvery || 1);

                      if (isDailyPattern) {
                        nextDate.setDate(nextDate.getDate() + multiplier);
                      } else if (isWeeklyPattern) {
                        nextDate.setDate(nextDate.getDate() + multiplier * 7);
                      } else if (isMonthlyPattern) {
                        // Handle month addition properly to avoid date drift
                        const baseYear = baseDate.getFullYear();
                        const baseMonth = baseDate.getMonth();
                        const baseDay = baseDate.getDate();

                        // Calculate target month and year
                        const totalMonths = baseMonth + multiplier;
                        const targetYear = baseYear + Math.floor(totalMonths / 12);
                        const targetMonth = totalMonths % 12;

                        // Create date with target year and month, and original day
                        nextDate = new Date(targetYear, targetMonth, baseDay);

                        // Check if the day overflowed (e.g., Jan 31 -> Feb 31 doesn't exist)
                        if (nextDate.getDate() !== baseDay) {
                          // Set to last day of the target month
                          nextDate = new Date(targetYear, targetMonth + 1, 0);
                        }
                      } else if (isYearlyPattern) {
                        nextDate.setFullYear(nextDate.getFullYear() + multiplier);
                      }
                      dates.push(format(nextDate, 'MMM d, yyyy'));
                    }
                    return `${dates.join(', ')}...`;
                  })()}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ ...figmaStyles.inputLabel, mb: 1 }}>Reminders</Typography>
            <FormSwitch
              name="multipleReminders"
              control={form.control}
              controlLabel="Set multiple reminders"
            />

            {multipleReminders ? (
              <Box sx={{ mt: 1 }}>
                {reminderIntervals.map((interval, index) => (
                  <React.Fragment key={`reminder-interval-${interval.value}-${interval.unit}`}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <FormTextField
                        name={`reminderIntervals.${index}.value`}
                        control={form.control}
                        textFieldProps={{
                          type: 'number',
                          sx: { ...figmaStyles.inputField, width: 80 },
                          inputProps: { min: 1 },
                        }}
                      />
                      <FormSelect
                        control={form.control}
                        options={reminderUnitOptions}
                        muiSelectProps={{
                          sx: { ...figmaStyles.inputField, minWidth: 120 },
                        }}
                        {...form.register(`reminderIntervals.${index}.unit`)}
                      />
                      <IconButton
                        onClick={() => removeReminderInterval(index)}
                        sx={{ color: 'error.main', p: 0.5 }}
                      >
                        <XCloseIcon />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        ml: 1,
                        mb: 1,
                        color: 'text.secondary',
                        fontSize: '12px',
                      }}
                    >
                      Reminder date:{' '}
                      {calculateReminderDate(
                        form.watch(`reminderIntervals.${index}.value`) || 0,
                        form.watch(`reminderIntervals.${index}.unit`) || ReminderUnit.DAYS_BEFORE
                      )}
                    </Typography>
                  </React.Fragment>
                ))}
                <Button
                  startIcon={<PlusIcon />}
                  onClick={addReminderInterval}
                  variant="text"
                  color="primary"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  Add reminder
                </Button>
              </Box>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <FormTextField
                  control={form.control}
                  textFieldProps={{
                    type: 'number',
                    sx: { ...figmaStyles.inputField, width: 80 },
                    inputProps: { min: 1 },
                  }}
                  {...form.register('reminderIntervals.0.value', {
                    setValueAs: value => (value === '' ? 1 : Number(value)),
                  })}
                />
                <FormSelect
                  control={form.control}
                  options={reminderUnitOptions}
                  muiSelectProps={{
                    sx: { ...figmaStyles.inputField, minWidth: 140 },
                  }}
                  {...form.register('reminderIntervals.0.unit')}
                />
              </Stack>
            )}
          </Box>
        </Box>

        <Box sx={figmaStyles.formSection}>
          <Typography variant="h6" sx={figmaStyles.sectionTitle}>
            Notifications
          </Typography>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifySelf}
                  onChange={e => setValue('notifySelf', e.target.checked)}
                />
              }
              label="Notify Myself"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          <Box sx={figmaStyles.inlineGroup}>
            <FormControlLabel
              checked={emailNotification}
              control={<Checkbox {...form.register('emailNotification')} />}
              label="Email"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              checked={smsNotification}
              control={<Checkbox {...form.register('smsNotification')} />}
              label="SMS"
              sx={figmaStyles.notificationLabel}
            />
            <FormControlLabel
              checked={dashboardNotification}
              control={<Checkbox {...form.register('dashboardNotification')} />}
              label="Dashboard"
              sx={figmaStyles.notificationLabel}
            />
          </Box>

          {notifyVendor && (
            <Box sx={{ mt: 1 }}>
              <Box sx={figmaStyles.inlineGroup}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Select Vendor</Typography>
                  <FormSelect
                    control={form.control}
                    options={vendors}
                    muiSelectProps={{
                      sx: figmaStyles.inputField,
                    }}
                    {...form.register('vendorId')}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={figmaStyles.inputLabel}>Vendor Email</Typography>
                  <FormTextField
                    name="vendorEmail"
                    control={form.control}
                    textFieldProps={{
                      fullWidth: true,
                      InputProps: {
                        placeholder: 'Email',
                      },
                      sx: figmaStyles.inputField,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography sx={figmaStyles.inputLabel}>Custom Message for Vendor</Typography>
                <FormTextField
                  name="vendorMessage"
                  control={form.control}
                  textFieldProps={{
                    fullWidth: true,
                    InputProps: {
                      placeholder: 'Message',
                    },
                    multiline: true,
                    rows: 2,
                    sx: figmaStyles.inputField,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </MuiPageWrapper>
    </ModalWithForm>
  );
};

export default CalendarEventCreateModal2;
